import React, { useState, useRef } from 'react';
import {
    StarIcon,
    PhotoIcon,
    XMarkIcon,
    CloudArrowUpIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { reviewsApi } from '../../utils/api';

/**
 * Composant formulaire pour créer ou modifier un avis produit
 * Inclut notation par étoiles, commentaire, et upload d'images
 */
const ReviewForm = ({ 
    productId, 
    existingReview = null, 
    onSubmit, 
    onCancel, 
    isSubmitting = false 
}) => {
    // États du formulaire
    const [formData, setFormData] = useState({
        rating: existingReview?.rating || 0,
        title: existingReview?.title || '',
        comment: existingReview?.comment || '',
        customerName: existingReview?.customer_name || '',
        customerEmail: existingReview?.customer_email || '',
        isAnonymous: existingReview?.is_anonymous || false
    });

    // États pour les images
    const [images, setImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState(
        existingReview?.images?.map(img => ({
            id: img.id,
            url: img.image_url,
            isExisting: true
        })) || []
    );
    const [uploadErrors, setUploadErrors] = useState([]);

    // États de validation
    const [errors, setErrors] = useState({});
    const [isHovering, setIsHovering] = useState(false);

    // Référence pour l'input de fichier
    const fileInputRef = useRef(null);

    // Gérer le changement de notation
    const handleRatingChange = (rating) => {
        setFormData(prev => ({ ...prev, rating }));
        if (errors.rating) {
            setErrors(prev => ({ ...prev, rating: null }));
        }
    };

    // Gérer les changements de champs texte
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const fieldValue = type === 'checkbox' ? checked : value;
        
        setFormData(prev => ({ ...prev, [name]: fieldValue }));
        
        // Effacer l'erreur du champ modifié
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    // Valider le formulaire
    const validateForm = () => {
        const newErrors = {};

        if (formData.rating === 0) {
            newErrors.rating = 'Veuillez attribuer une note';
        }

        if (!formData.comment?.trim()) {
            newErrors.comment = 'Un commentaire est requis';
        }

        if (formData.comment?.trim().length < 10) {
            newErrors.comment = 'Le commentaire doit contenir au moins 10 caractères';
        }

        if (!formData.isAnonymous && !formData.customerName?.trim()) {
            newErrors.customerName = 'Le nom est requis pour les avis non anonymes';
        }

        if (formData.customerEmail && !isValidEmail(formData.customerEmail)) {
            newErrors.customerEmail = 'Adresse email invalide';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Valider l'email
    const isValidEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    // Gérer la sélection de fichiers
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        processFiles(files);
        e.target.value = ''; // Reset input
    };

    // Gérer le drag & drop
    const handleDrop = (e) => {
        e.preventDefault();
        setIsHovering(false);
        const files = Array.from(e.dataTransfer.files);
        processFiles(files);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsHovering(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsHovering(false);
    };

    // Traiter les fichiers sélectionnés
    const processFiles = (files) => {
        const maxFiles = 5;
        const maxSize = 5 * 1024 * 1024; // 5MB
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

        const currentImageCount = images.length + imagePreviews.filter(p => p.isExisting).length;
        const availableSlots = maxFiles - currentImageCount;

        if (files.length > availableSlots) {
            setUploadErrors(prev => [...prev, `Maximum ${maxFiles} images autorisées`]);
            files = files.slice(0, availableSlots);
        }

        const newImages = [];
        const newPreviews = [];
        const newErrors = [];

        files.forEach((file, index) => {
            // Vérifier le type
            if (!allowedTypes.includes(file.type)) {
                newErrors.push(`${file.name}: Type de fichier non supporté`);
                return;
            }

            // Vérifier la taille
            if (file.size > maxSize) {
                newErrors.push(`${file.name}: Fichier trop volumineux (max 5MB)`);
                return;
            }

            // Créer l'aperçu
            const reader = new FileReader();
            reader.onload = (e) => {
                const preview = {
                    id: `new_${Date.now()}_${index}`,
                    url: e.target.result,
                    isExisting: false,
                    file: file
                };
                
                setImagePreviews(prev => [...prev, preview]);
            };
            reader.readAsDataURL(file);

            newImages.push(file);
        });

        if (newErrors.length > 0) {
            setUploadErrors(prev => [...prev, ...newErrors]);
        }

        if (newImages.length > 0) {
            setImages(prev => [...prev, ...newImages]);
        }
    };

    // Supprimer une image
    const removeImage = (imageId) => {
        const imageToRemove = imagePreviews.find(p => p.id === imageId);
        
        if (imageToRemove?.isExisting) {
            // Image existante, juste la retirer de la prévisualisation
            setImagePreviews(prev => prev.filter(p => p.id !== imageId));
        } else {
            // Nouvelle image, retirer du tableau des fichiers aussi
            setImagePreviews(prev => prev.filter(p => p.id !== imageId));
            setImages(prev => prev.filter((_, index) => {
                const previewIndex = imagePreviews.findIndex(p => p.id === imageId);
                return index !== previewIndex - imagePreviews.filter(p => p.isExisting).length;
            }));
        }
    };

    // Effacer les erreurs d'upload
    const clearUploadErrors = () => {
        setUploadErrors([]);
    };

    // Soumettre le formulaire
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            // Préparer les données à envoyer
            const submitData = {
                productId,
                rating: formData.rating,
                title: formData.title?.trim() || null,
                comment: formData.comment?.trim(),
                customerName: formData.isAnonymous ? null : formData.customerName?.trim(),
                customerEmail: formData.customerEmail?.trim() || null,
                isAnonymous: formData.isAnonymous
            };

            // Appeler la fonction onSubmit avec les données et les images
            if (onSubmit) {
                await onSubmit(submitData, images);
            }

        } catch (error) {
            console.error('Erreur lors de la soumission:', error);
            setErrors({ submit: error.message || 'Erreur lors de la création de l\'avis' });
        }
    };

    // Rendu des étoiles pour la notation
    const renderRatingStars = () => {
        return (
            <div className="flex items-center space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                    <button
                        key={star}
                        type="button"
                        onClick={() => handleRatingChange(star)}
                        className="focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
                    >
                        {star <= formData.rating ? (
                            <StarIconSolid className="h-8 w-8 text-yellow-400 hover:text-yellow-500 transition-colors" />
                        ) : (
                            <StarIcon className="h-8 w-8 text-gray-300 hover:text-yellow-400 transition-colors" />
                        )}
                    </button>
                ))}
                {formData.rating > 0 && (
                    <span className="ml-2 text-sm font-medium text-gray-700">
                        {formData.rating} étoile{formData.rating > 1 ? 's' : ''}
                    </span>
                )}
            </div>
        );
    };

    return (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {existingReview ? 'Modifier l\'avis' : 'Laisser un avis'}
                </h3>
                <p className="text-sm text-gray-600">
                    Partagez votre expérience avec ce produit
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Notation */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Note <span className="text-red-500">*</span>
                    </label>
                    {renderRatingStars()}
                    {errors.rating && (
                        <p className="mt-1 text-sm text-red-600">{errors.rating}</p>
                    )}
                </div>

                {/* Titre (optionnel) */}
                <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                        Titre de l'avis (optionnel)
                    </label>
                    <input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="Résumez votre avis en quelques mots"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        maxLength={200}
                    />
                    {formData.title && (
                        <p className="mt-1 text-sm text-gray-500">
                            {formData.title.length}/200 caractères
                        </p>
                    )}
                </div>

                {/* Commentaire */}
                <div>
                    <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
                        Commentaire <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        id="comment"
                        name="comment"
                        value={formData.comment}
                        onChange={handleInputChange}
                        rows={4}
                        placeholder="Décrivez votre expérience avec ce produit..."
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        maxLength={2000}
                    />
                    <div className="mt-1 flex justify-between text-sm text-gray-500">
                        <span>{formData.comment.length}/2000 caractères</span>
                        {formData.comment.length < 10 && (
                            <span className="text-orange-600">
                                Minimum 10 caractères
                            </span>
                        )}
                    </div>
                    {errors.comment && (
                        <p className="mt-1 text-sm text-red-600">{errors.comment}</p>
                    )}
                </div>

                {/* Upload d'images */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Images (optionnel) - Maximum 5
                    </label>
                    
                    {/* Zone de drop */}
                    <div
                        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                            isHovering 
                                ? 'border-primary-400 bg-primary-50' 
                                : 'border-gray-300 hover:border-gray-400'
                        }`}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                    >
                        <CloudArrowUpIcon className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm text-gray-600 mb-2">
                            Glissez vos images ici ou{' '}
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="text-primary-600 hover:text-primary-500 font-medium"
                            >
                                cliquez pour parcourir
                            </button>
                        </p>
                        <p className="text-xs text-gray-500">
                            PNG, JPG, GIF, WebP jusqu'à 5MB chacune
                        </p>
                    </div>

                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                    />

                    {/* Erreurs d'upload */}
                    {uploadErrors.length > 0 && (
                        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                            <div className="flex">
                                <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mt-0.5 mr-2" />
                                <div className="flex-1">
                                    {uploadErrors.map((error, index) => (
                                        <p key={index} className="text-sm text-red-700">{error}</p>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={clearUploadErrors}
                                        className="mt-1 text-xs text-red-600 hover:text-red-500 underline"
                                    >
                                        Effacer
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Prévisualisation des images */}
                    {imagePreviews.length > 0 && (
                        <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-3">
                            {imagePreviews.map((preview) => (
                                <div key={preview.id} className="relative group">
                                    <img
                                        src={preview.url}
                                        alt="Aperçu"
                                        className="w-full h-20 object-cover rounded-lg border border-gray-200"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeImage(preview.id)}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100"
                                    >
                                        <XMarkIcon className="h-4 w-4" />
                                    </button>
                                    {preview.isExisting && (
                                        <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-xs px-1 rounded">
                                            Existante
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Informations du client */}
                <div className="border-t pt-6">
                    <div className="flex items-center mb-4">
                        <input
                            type="checkbox"
                            id="isAnonymous"
                            name="isAnonymous"
                            checked={formData.isAnonymous}
                            onChange={handleInputChange}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <label htmlFor="isAnonymous" className="ml-2 text-sm text-gray-700">
                            Publier anonymement
                        </label>
                    </div>

                    {!formData.isAnonymous && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="customerName" className="block text-sm font-medium text-gray-700 mb-1">
                                    Nom <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="customerName"
                                    name="customerName"
                                    value={formData.customerName}
                                    onChange={handleInputChange}
                                    placeholder="Votre nom"
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                />
                                {errors.customerName && (
                                    <p className="mt-1 text-sm text-red-600">{errors.customerName}</p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="customerEmail" className="block text-sm font-medium text-gray-700 mb-1">
                                    Email (optionnel)
                                </label>
                                <input
                                    type="email"
                                    id="customerEmail"
                                    name="customerEmail"
                                    value={formData.customerEmail}
                                    onChange={handleInputChange}
                                    placeholder="votre@email.com"
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                />
                                {errors.customerEmail && (
                                    <p className="mt-1 text-sm text-red-600">{errors.customerEmail}</p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Erreur de soumission */}
                {errors.submit && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-700">{errors.submit}</p>
                    </div>
                )}

                {/* Boutons d'action */}
                <div className="flex justify-end space-x-3 pt-6 border-t">
                    {onCancel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={isSubmitting}
                            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                        >
                            Annuler
                        </button>
                    )}
                    <button
                        type="submit"
                        disabled={isSubmitting || formData.rating === 0}
                        className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                        {isSubmitting ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                                Publication...
                            </>
                        ) : (
                            existingReview ? 'Modifier l\'avis' : 'Publier l\'avis'
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ReviewForm;